import { useCallback, useSyncExternalStore } from "react";

/**
 * Subscribes to a CSS media query and re-renders when it flips.
 *
 * Used for the handful of decisions CSS cannot make on its own - chiefly which
 * ARIA semantics the sidebar should carry, since it is a plain navigation column
 * at desktop widths and a modal drawer below the breakpoint.
 *
 * Falls back to `false` where `matchMedia` is unavailable (jsdom under vitest),
 * which is the conservative answer: the drawer semantics stay wired up rather
 * than silently disappearing.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return () => {};
      }

      const mediaQueryList = window.matchMedia(query);
      mediaQueryList.addEventListener("change", onStoreChange);
      return () => mediaQueryList.removeEventListener("change", onStoreChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
